import EncounterSerializer from "issara-sdk/types/EncounterSerializer_core";
import PatientAppointmentSerializer from "issara-sdk/types/PatientAppointmentSerializer_apps_QUE";
import PatientSerializer from "issara-sdk/types/PatientSerializer_apps_REG";

import { calculateAgeAndDOB } from "react-lib/apps/HISV3/common/CommonInterface";

import { stripHtml } from "../patient/resultModelV4";

// ---------- เมนู ----------

export type RegistrationMenuKey = "pos" | "info" | "encounter" | "history";

export const REGISTRATION_MENU: {
  key: RegistrationMenuKey;
  title: string;
  subtitle: string;
  icon: string;
}[] = [
  {
    key: "pos",
    title: "เลือกซื้อสินค้าและบริการ",
    subtitle: "POS · อาหารเสริม · แพ็กเกจ",
    icon: "🛒",
  },
  { key: "info", title: "ข้อมูลผู้รับบริการ", subtitle: "ข้อมูลบัตร · ที่อยู่", icon: "👤" },
  { key: "history", title: "ดูประวัติการรักษา", subtitle: "Patient History Viewer", icon: "📋" },
  { key: "encounter", title: "เปิด Encounter", subtitle: "เลือกนัด / Walk-in", icon: "🩺" },
];

/** เมนู POS (MENU_KEYS.PRODUCT_SERVICE ใน CardMeBillingMenuContent) */
export const POS_MENU = "เลือกสินค้าและบริการ";

// ---------- ค้นหาผู้รับบริการ ----------

/** แถวของ PatientList.list (SetPatientSequence.patientList) */
export type PatientListItem = {
  id: number;
  address?: string;
  birthdate?: string;
  citizen_passport?: string;
  full_name?: string;
  hn?: string;
  mother?: string;
  old_name?: string;
  phone_num?: string;
  present_mobile_no?: string;
  /** VERIFIED / NOT_VERIFIED (ยืนยันตัวตนผ่านแอป) */
  profile_status?: string;
  tel_num?: string;
};

/** SetPatientSequence.patient (PatientDetailView) */
export type PatientDetail = Partial<PatientSerializer> & { phone_num?: string };

export type PatientSummary = {
  id?: number;
  hn: string;
  image: string;
  name: string;
  rows: { key: string; value: string }[];
};

export const getPatientSummary = (patient: PatientDetail): PatientSummary => {
  const present = patient.present_address;
  const age = calculateAgeAndDOB(patient.full_age).fullage_th;

  return {
    id: patient.id,
    hn: patient.hn ?? "",
    image: patient.profile_image?.image ?? "",
    name: patient.full_name ?? "",
    rows: [
      { key: "เพศ", value: patient.gender_name || "-" },
      { key: "อายุ", value: age || "-" },
      { key: "วันเกิด", value: patient.birthdate || "-" },
      { key: "เบอร์โทร", value: present?.tel_mobile || patient.phone_num || "-" },
      { key: "E-mail", value: present?.email || patient.email || "-" },
      { key: "Citizen ID", value: patient.citizen_passport || patient.citizen_no || "-" },
    ],
  };
};

// ---------- เปิดประวัติหมู่ ----------

/** แถวเปิดประวัติหมู่: จากไฟล์ xlsx หรือผลของ register_emergency */
export type GroupRow = {
  no: number;
  first_name_th: string;
  gender: string;
  hn: string;
  last_name_th: string;
  pre_name: string;
};

/** แถวจากไฟล์ xlsx (หัวคอลัมน์ คำนำหน้า / ชื่อ / นามสกุล / เพศ) */
export const formatGroupRowsFromSheet = (rows: Record<string, unknown>[]): GroupRow[] =>
  rows.map((row, index) => ({
    ...row,
    no: index + 1,
    first_name_th: String(row["ชื่อ"] ?? ""),
    gender: String(row["เพศ"] ?? ""),
    hn: "",
    last_name_th: String(row["นามสกุล"] ?? ""),
    pre_name: String(row["คำนำหน้า"] ?? ""),
  }));

/** ผลจาก register_emergency (patientEmergencyList) มี hn ของแต่ละคนแล้ว */
export const formatGroupRows = (list: Partial<GroupRow>[]): GroupRow[] =>
  list.map((item, index) => ({
    no: index + 1,
    first_name_th: item.first_name_th ?? "",
    gender: item.gender ?? "",
    hn: item.hn ?? "",
    last_name_th: item.last_name_th ?? "",
    pre_name: item.pre_name ?? "",
  }));

// ---------- เปิด Encounter ----------

/** แถวของ appointmentEncounterList (CreateUpdateEncounter) */
export type AppointmentItem = Omit<Partial<PatientAppointmentSerializer>, "appointment_type"> & {
  appointment_type?: number | string;
  children?: { summary_detail?: string }[];
  detail?: string;
  doctor?: number | null;
  doctor_name?: string;
  order_dict?: Record<string, string>;
};

export type EncounterAppointmentRow = {
  id: number;
  date: string;
  detail: string;
  division: string;
  doctor: string;
  orders: string;
  time: string;
};

export type EncounterItem = Partial<EncounterSerializer> & { id: number };

// ---------- เปิด Encounter (Walk-in) ----------

// Reuse generated encounter fields; records below are view models for patient history.
export type ClinicalTermInput = NonNullable<EncounterSerializer["special_request_data_list"]>[number];
export type WalkInOpeningData = Pick<EncounterSerializer,
  "arrive_status" | "chief_complaints" | "channel_data_list" | "special_request_data_list">;
export type TeamMessageOpeningData = Pick<WalkInOpeningData, "channel_data_list" | "special_request_data_list">;
export type TeamMessageRecord = {
  items: { id: number; name: string }[];
  recorded_by?: string | null;
  recorded_at?: string | null;
  encounter?: number | null;
};
export type WalkInTerm = { id: number; name: string; code?: string };
export type WalkInOptions = {
  patientId: number;
  divisionId?: number;
  arrive: WalkInTerm[];
  arriveLoaded?: boolean;
  requestsLoaded?: boolean;
  channels: WalkInTerm[];
  requests: WalkInTerm[];
  latestChannel?: TeamMessageRecord;
  specialRequests?: TeamMessageRecord;
};
export const clinicalTermInputs = (values: string[], options: WalkInTerm[]): ClinicalTermInput[] => {
  const result: ClinicalTermInput[] = [];
  const seen = new Set<string>();
  for (const value of values) {
    const name = value.trim();
    if (!name) continue;
    const option = options.find((item) => String(item.id) === value)
      ?? options.find((item) => item.name.trim().toLowerCase() === name.toLowerCase());
    const entry = option ? { id: option.id } : { name };
    const key = option ? `id:${option.id}` : `name:${name.toLowerCase()}`;
    if (!seen.has(key)) result.push(entry);
    seen.add(key);
  }
  return result;
};
/** Include historical terms from other divisions so selections retain their names. */
export const requestTerms = (options?: WalkInOptions, baseline?: TeamMessageRecord): WalkInTerm[] =>
  Array.from(new Map([
    ...(baseline?.items ?? []), ...(options?.specialRequests?.items ?? []), ...(options?.requests ?? []),
  ].map((term) => [term.id, term])).values());

const sameClinicalTermInputs = (left: ClinicalTermInput[], right: ClinicalTermInput[]): boolean => {
  const keys = (items: ClinicalTermInput[]) => items.map((item) => "id" in item
    ? `id:${item.id}` : `name:${(item.name ?? "").trim().toLowerCase()}`).sort();
  return JSON.stringify(keys(left)) === JSON.stringify(keys(right));
};

/** Shared by appointment and Walk-in; unchanged or unknown requests are omitted. */
export const teamMessageOpeningData = (
  channel: string, requests: string[], options?: WalkInOptions,
  baseline: TeamMessageRecord | undefined = options?.specialRequests,
): TeamMessageOpeningData => {
  const payload: TeamMessageOpeningData = {
    ...(channel && options?.latestChannel && !options.latestChannel.items.length
      ? { channel_data_list: clinicalTermInputs([channel], options.channels) } : {}),
  };
  // Unknown/failed/loading history must never clear a patient's existing requests.
  if (!options?.specialRequests || options.requestsLoaded === false || !baseline) return payload;
  const terms = requestTerms(options, baseline);
  const selected = clinicalTermInputs(requests, terms);
  const defaults = clinicalTermInputs(baseline.items.map((item) => String(item.id)), terms);
  if (!sameClinicalTermInputs(selected, defaults)) payload.special_request_data_list = selected;
  return payload;
};

export const walkInOpeningData = (
  arrive: string, chiefComplaint: string, channel: string, requests: string[], options?: WalkInOptions,
  baseline: TeamMessageRecord | undefined = options?.specialRequests,
): WalkInOpeningData => {
  const payload: WalkInOpeningData = {
    ...(options?.arriveLoaded && options.arrive.some((term) => term.code === arrive) && arrive
      ? { arrive_status: arrive } : {}),
    chief_complaints: chiefComplaint.slice(0, 255),
    ...teamMessageOpeningData(channel, requests, options, baseline),
  };
  // With no arrival, keep the existing nonempty snapshot when sending a chief complaint.
  // Never manufacture [] just to force assessment creation.
  if (!payload.special_request_data_list && !payload.arrive_status && chiefComplaint.trim()
    && options?.specialRequests && options.requestsLoaded !== false && baseline) {
    const defaults = clinicalTermInputs(baseline.items.map((item) => String(item.id)), requestTerms(options, baseline));
    if (defaults.length) payload.special_request_data_list = defaults;
  }
  return payload;
};

/** นัดที่เปิด Encounter ได้: มีวัน เวลา แพทย์ หรือเป็นนัดแพ็กเกจ / กายภาพ */
export const formatAppointmentRows = (list: AppointmentItem[]): EncounterAppointmentRow[] =>
  list
    .filter((item) => {
      const hasDetails = !!(item.date && item.start_time && item.doctor);
      const isPackage = Number(item.appointment_type) === 3;
      const isRehab = Object.values(item.order_dict ?? {}).includes("rehabilitationorder");

      return !!item.id && (hasDetails || isPackage || isRehab);
    })
    .map((item) => ({
      id: item.id as number,
      date: item.date ?? "",
      detail: item.is_telemed ? "นัดปรึกษาแพทย์ออนไลน์" : (item.detail ?? ""),
      division: item.division_name ?? "",
      doctor: item.doctor_name ?? "",
      orders: (item.children ?? [])
        .map((child) => stripHtml(child.summary_detail))
        .filter(Boolean)
        .join(" · "),
      time: item.start_time ?? "",
    }));
